import { Component, OnInit, Input, Inject, OnDestroy } from '@angular/core';
import {
  UntypedFormGroup,
  Validators,
  AbstractControl,
  UntypedFormArray,
  FormBuilder,
  FormControl,
  FormArray
} from '@angular/forms';

import moment from 'moment';

import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  first,
  map,
  mergeMap,
  take,
  takeUntil
} from 'rxjs/operators';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ActivatedRoute } from '@angular/router';
import { Enum, EnumWithDefault, KeyValuePair } from '../../../../models/base';
import { BaseComponent } from '../../../../components/base/base.component';
import { AdminService } from '../../../../services/admin.service';
import {
  CompanyLocalTaxConfig,
  CompanyTaxCodeConfig,
  SchoolDistrict
} from '../../../../models/tax-config';
import { CompaniesService } from '../../../../services/companies.service';
import { EmployeeStatus } from '../../../../models/person-status';
import { AuthService } from '../../../../services/auth.service';
import {
  EmployeeTax,
  EmployeeTaxes,
  EmployeeTaxExemptions,
  StateTaxExemption
} from '../../../../models/employee-tax';
import { ModalService } from '../../../../services/modal.service';
import { Person } from '../../../../models/person';
import { InstanceTypes } from '../../../../models/instance-types';
import { Select, Store } from '@ngxs/store';
import {
  PersonalDetailsFormSelectors,
  PersonEditorState,
  TaxesFormSelectors
} from '../../../../state/PersonEditorState/PersonEditor.state';
import { forkJoin, Observable, Subject } from 'rxjs';
import { EnumService } from '../../../../services/enum.service';
import { ClientSelectionState } from '../../../../state/ClientSelectionState/ClientSelection.state';
import { EmploymentTypes } from '../../../../models/employment-types';
import { da } from 'date-fns/locale';
import {
  SetCurrentTabDirty,
  SetCurrentTabStatus,
  SetHeader
} from '../../../../state/PersonEditorState/PersonEditor.actions';
import { SetIsNonUSAddress } from '../../../../state/PersonEditorState/PersonEditor.PersonalDetailsForm.actions';
import {
  SetAddlStateAddlAllowances,
  SetAddlStateAddlWithholdingType,
  SetAddlStateAddlWithholdingValue,
  SetAddlStateFilingStatus,
  SetAddlStateWithholdingAllowance,
  SetChildTaxCredit,
  SetExpireDate,
  SetFederalAddlWithholdingType,
  SetFederalAddlWithholdingValue,
  SetFederalFilingStatus,
  SetFederalWithholdingAllowance,
  SetFUTAExemptionReason,
  SetHasDisLeaveExempt,
  SetHasFamilyLeaveExempt,
  SetHasFUTAExemption,
  SetHasMEDCExemption,
  SetHasMedLeaveExempt,
  SetHasSSExemption,
  SetHasSUIExemption,
  SetIsExemptFromWithholding,
  SetIsHigherWithholding,
  SetJ1F1Visa,
  SetLocals,
  SetOtherIncome,
  SetStateAddlAllowances,
  SetStateAddlWithholdingType,
  SetStateAddlWithholdingValue,
  SetStateFilingStatus,
  SetStateWithholdingAllowance,
  SetTaxesFormState,
  SetW4Version,
  SetWageDeduction,
  SetWithHoldingState
} from '../../../../state/PersonEditorState/PersonEditor.TaxesForm.actions';
import _ from 'underscore';
import { SetJobDetailsFormState } from '../../../../state/PersonEditorState/PersonEditor.JobDetailsForm.actions';

@Component({
  selector: 'app-taxes-tab',
  templateUrl: './taxes-tab.component.html',
  styleUrls: ['./taxes-tab.component.css'],
  standalone: false
})
export class TaxesTabComponent extends BaseComponent implements OnInit, OnDestroy {
  // forms
  taxesForm: UntypedFormGroup;

  // enums
  futaReasons: Enum[] = [];

  @Select(PersonEditorState.getPerson) person: Observable<Person>;
  @Select(PersonEditorState.getReadOnly) readOnly: Observable<boolean>;
  @Select(PersonEditorState.getEmployeeTaxes) taxes: Observable<EmployeeTaxes>;

  bothWHStateOpts = [];
  clientCode: string;
  // ngModel for local tax county/muni.
  countyLocalsModel: [Record<number, Record<string, string>>] = [{}];
  livesInState: Enum;
  localsByCounty: Record<string, Record<string, SchoolDistrict[]>>;
  localTaxConfigs: CompanyLocalTaxConfig[] = [];
  invalidResidentConfigs: CompanyLocalTaxConfig[] = [];
  invalidWorksInConfigs: CompanyLocalTaxConfig[] = [];
  invalidTaxMessage: string;
  localTaxStateNames: string[] = [];
  localTaxStateUrls: string[] = [];
  requiredLocalTaxText = '';
  selectByCounty = {}; // PA local tax
  selResidentState: string;
  selWHStateId = ''; // state abbrev or 'Both StateA StateB'
  showJ1F1VisaWarning = false;
  showJ1F1ExpirationMsg = false;
  withHoldingStateIds = [];
  worksInState: Enum;

  private destroy$ = new Subject<void>();

  get isClientPortal() {
    return this.env.instanceType == InstanceTypes.ClientPortal;
  }

  constructor(
    private adminService: AdminService,
    public authService: AuthService,
    private companyService: CompaniesService,
    private modalService: ModalService,
    private ngbModalService: NgbModal,
    private route: ActivatedRoute,
    @Inject('env') private env: any,
    private enumService: EnumService,
    private store: Store,
    private formBuilder: FormBuilder
  ) {
    super();
  }

  get f() {
    return this.taxesForm.controls;
  }

  get l() {
    if (this.taxesForm.controls.Locals) {
      return this.taxesForm.controls.Locals as UntypedFormArray;
    } else {
      return new UntypedFormArray([]);
    }
  }

  get lControls() {
    return this.l.controls as UntypedFormGroup[];
  }

  getControl(name: string) {
    return this.taxesForm.get(name) as FormControl;
  }

  getLocalsControl(name: string, index: number) {
    const locals = this.taxesForm.get('Locals') as FormArray;
    return locals.at(index).get(name) as FormControl;
  }

  canHaveAdditionalAllowances(stateAbbrev: string): Observable<boolean> {
    return this.getStateIncomeTaxConfig(stateAbbrev).pipe(
      map(config => config && config.CanHaveAdditionalAllowances)
    );
  }

  checkVisaExpiration() {
    // if J1/F1 expire date has been reached or passed and the SS &/or MEDC flags are set - warn user
    const d = this.f.ExpireDate.value;

    if (this.f.J1F1Visa.value == '0' || !d) {
      this.showJ1F1ExpirationMsg = false;
    } else if (
      moment(`${d.year}-${d.month}-${d.day}`, 'YYYY-MM-DD').isSameOrBefore(new Date(), 'day')
    ) {
      this.showJ1F1ExpirationMsg = true;
      this.f.ExpireDate.markAsTouched(); // show error on tab load
    } else {
      this.showJ1F1ExpirationMsg = false;
    }
  }

  clearStateTaxElections() {
    this.selWHStateId = '';

    this.taxesForm.patchValue({
      WithHoldingState: '',
      StateFilingStatus: '',
      StateWithholdingAllowance: '',
      StateAddlAllowances: '',
      StateAddlWithholdingType: 'null',
      StateAddlWithholdingValue: '',
      AddlStateFilingStatus: '',
      AddlStateWithholdingAllowance: '',
      AddlStateAddlAllowances: '',
      AddlStateAddlWithholdingType: 'null',
      AddlStateAddlWithholdingValue: ''
    });

    this.f.WithHoldingState.clearValidators();
    this.f.WithHoldingState.updateValueAndValidity();

    this.f.StateAddlWithholdingValue.clearValidators();
    this.f.StateAddlWithholdingValue.updateValueAndValidity();

    this.f.AddlStateAddlWithholdingValue.clearValidators();
    this.f.AddlStateAddlWithholdingValue.updateValueAndValidity();
  }

  getFederalFilingStatuses(): Observable<EnumWithDefault[]> {
    return this.store.selectOnce(PersonEditorState.getCompanyTaxConfigs).pipe(
      map(companyTaxConfigs => {
        const tax = companyTaxConfigs.find(x => x.EntityType === 'F');
        return tax && tax.FilingStatuses ? tax.FilingStatuses : null;
      })
    );
  }

  getLocalConfig(ctrl): any {
    return this.localTaxConfigs.find(x => x.LocalTypeId == ctrl.value.LocalTypeId) || {};
  }

  getLocalCounties() {
    return this.localsByCounty ? Object.keys(this.localsByCounty) : [];
  }

  getLocalFilingStatuses(group: UntypedFormGroup) {
    const fs = this.getLocalConfig(group).FilingStatuses;
    return group.value.TaxCode && fs
      ? fs.filter(x => x.TaxCode.trim() == group.value.TaxCode.trim())
      : [];
  }

  getLocalTaxPropValue(localTypeId: number, prop: string, config: CompanyLocalTaxConfig) {
    return this.store.selectOnce(PersonEditorState.getEmployeeTaxes).pipe(
      map(taxes => {
        const tax = taxes.Locals.find(x => x.LocalTypeId == localTypeId);
        if (!tax) {
          return;
        }

        let value = tax && (tax[prop] || tax[prop] === 0) ? tax[prop] : null;

        if (prop === 'TaxCode') {
          // return tax code description as well
          return value + ' - ' + config.TaxCodes.find(x => x.Key == value).Value;
        } else {
          return value;
        }
      })
    );
  }

  getMunicipalities(county: string) {
    return county ? Object.keys(this.localsByCounty[county]) : [];
  }

  getMuniDescription(county: string, muni: string) {
    // NOTE: this is less than ideal, but working for now. if that changes,
    // CP devs will have to pass muni description by itself
    let residentTaxName =
      county && muni ? this.localsByCounty[county][muni][0].ResidentTaxName : '';
    return residentTaxName.split('-')[0];
  }

  getSchoolDistricts(county: string, muni: string): SchoolDistrict[] {
    return county && muni ? this.localsByCounty[county][muni] : [];
  }

  getStateIncomeFilingStatuses(stateAbbrev: string): Observable<EnumWithDefault[]> {
    return this.getStateIncomeTaxConfig(stateAbbrev).pipe(
      map(config => {
        if (config && config.FilingStatuses) {
          return config.FilingStatuses;
        } else {
          return [];
        }
      })
    );
  }

  getStateIncomeTaxConfig(stateAbbrev: string): Observable<CompanyTaxCodeConfig> {
    return this.store.selectOnce(PersonEditorState.getCompanyTaxConfigs).pipe(
      mergeMap(companyTaxConfigs => {
        return this.store.selectOnce(PersonEditorState.getEmployeeTaxes).pipe(
          map(employeeTaxes => {
            return { companyTaxConfigs, employeeTaxes };
          })
        );
      }),
      map(taxInfo => {
        // Bug 4990 - filter tax configs so we're only using income configs,
        // and excluding inactive configs unless the user is set up to use them
        const configFilter = (config: CompanyTaxCodeConfig) => {
          // first make sure we match on stateAbbreviation and tax type is 'I'
          if (config.StateAbbreviation == stateAbbrev && config.TaxType == 'I') {
            // if it's inactive, we still need to include it if the user is
            // set up for it
            if (!config.IsActive) {
              const empTax = taxInfo.employeeTaxes.FedAndState.find(
                t => t.TaxCode == config.TaxCode
              );
              return empTax && empTax.IsActive;
            } else {
              return true;
            }
          } else {
            return false;
          }
        };

        const c = taxInfo.companyTaxConfigs.find(configFilter);
        if (c) {
          return c;
        } else {
          return null;
        }
      })
    );
  }

  getStateName(id): Observable<string> {
    return this.store.selectOnce(PersonEditorState.getStates).pipe(
      map(stateList => {
        if (!id) {
          return '';
        } else if (id.indexOf('Both') > -1) {
          const states = id.replace('Both ', '').split(' ');
          const stateA = stateList.find(x => x.Id == states[0]);
          const stateB = stateList.find(x => x.Id == states[1]);
          return `Both ${stateA.Value} and ${stateB.Value}`;
        } else {
          return stateList.find(x => x.Id === id)?.Value;
        }
      })
    );
  }

  isStateIncomeWithHolding(stateAbbrev: string): Observable<boolean> {
    return this.getStateIncomeTaxConfig(stateAbbrev).pipe(
      map(config => config && config.IsIncomeWithholdingState)
    );
  }

  ngOnInit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;
      const obs: Observable<any>[] = [
        this.enumService.get('FUTAEXEMPT'),
        this.store.selectOnce(PersonEditorState.getEditorMode),
        this.store.selectOnce(PersonEditorState.getPerson)
      ];

      forkJoin(obs).subscribe(results => {
        this.futaReasons = results[0];
        this.init(results[1], results[2]);
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private init(editorMode: string, person: Person) {
    this.store.selectOnce(TaxesFormSelectors.taxesFormModel).subscribe(taxesForm => {
      if (editorMode == 'add') {
        this.store.dispatch(new SetHeader());
      }
      this.taxesForm = this.formBuilder.group({
        FederalFilingStatus: new FormControl(taxesForm.FederalFilingStatus, Validators.required),
        FederalWithholdingAllowance: new FormControl(taxesForm.FederalWithholdingAllowance, [
          Validators.maxLength(2),
          Validators.pattern('[0-9]*$')
        ]),
        FederalAddlWithholdingType: new FormControl(taxesForm.FederalAddlWithholdingType ?? 'null'),
        FederalAddlWithholdingValue: new FormControl(taxesForm.StateAddlWithholdingValue, [
          Validators.max(9999999.99)
        ]),
        HasSSExemption: new FormControl(taxesForm.HasSSExemption),
        HasMEDCExemption: new FormControl(taxesForm.HasMEDCExemption),
        HasSUIExemption: new FormControl(taxesForm.HasSUIExemption),
        HasFUTAExemption: new FormControl(taxesForm.HasFUTAExemption),
        HasDisLeaveExempt: new FormControl(taxesForm.HasDisLeaveExempt),
        HasFamilyLeaveExempt: new FormControl(taxesForm.HasFamilyLeaveExempt),
        HasMedLeaveExempt: new FormControl(taxesForm.HasMedLeaveExempt),
        FUTAExemptionReason: new FormControl(taxesForm.FUTAExemptionReason),
        J1F1Visa: new FormControl(taxesForm.J1F1Visa),
        ExpireDate: new FormControl(taxesForm.ExpireDate),
        WithHoldingState: new FormControl(taxesForm.WithHoldingState),
        StateFilingStatus: new FormControl(taxesForm.StateFilingStatus),
        StateWithholdingAllowance: new FormControl(taxesForm.StateWithholdingAllowance),
        StateAddlAllowances: new FormControl(taxesForm.StateAddlAllowances),
        StateAddlWithholdingType: new FormControl(taxesForm.FederalAddlWithholdingType ?? 'null'),
        StateAddlWithholdingValue: new FormControl(taxesForm.StateAddlWithholdingValue ?? '', [
          Validators.max(9999999.99)
        ]),
        AddlStateFilingStatus: new FormControl(taxesForm.AddlStateFilingStatus),
        AddlStateWithholdingAllowance: new FormControl(taxesForm.AddlStateWithholdingAllowance),
        AddlStateAddlAllowances: new FormControl(taxesForm.AddlStateAddlAllowances),
        AddlStateAddlWithholdingType: new FormControl(
          taxesForm.AddlStateAddlWithholdingType ?? 'null'
        ),
        AddlStateAddlWithholdingValue: new FormControl(
          taxesForm.AddlStateAddlWithholdingValue ?? '',
          [Validators.max(9999999.99)]
        ),
        IsExemptFromWithholding: new FormControl(taxesForm.IsExemptFromWithholding),
        W4Version: new FormControl(taxesForm.W4Version),
        IsHigherWithholding: new FormControl(taxesForm.IsHigherWithholding),
        ChildTaxCredit: new FormControl(taxesForm.ChildTaxCredit),
        OtherIncome: new FormControl(taxesForm.OtherIncome),
        WageDeduction: new FormControl(taxesForm.WageDeduction),
        Locals: this.formBuilder.array(
          taxesForm.Locals.map(local => {
            return this.formBuilder.group({
              LocalTypeId: new FormControl(local.LocalTypeId, Validators.required),
              TaxCode: new FormControl(local.TaxCode, Validators.required),
              County: new FormControl(local.County),
              Municipality: new FormControl(local.Municipality),
              SchoolDistrict: new FormControl(local.SchoolDistrict),
              FilingStatus: new FormControl(local.FilingStatus),
              WithholdingAllowances: this.formBuilder.control(local.WithholdingAllowances)
            });
          })
        )
      });
      this.store
        .select(PersonEditorState.triggerMarkFormPristine)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.taxesForm.markAsPristine();
          this.taxesForm.markAsUntouched();
          this.store.dispatch(new SetCurrentTabDirty(false));
        });
      this.taxesForm.statusChanges
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(status => {
          this.store.dispatch(new SetTaxesFormState(status));
          if (status != null) {
            this.store.dispatch(new SetCurrentTabStatus(status));
          }
        });
      this.store.selectOnce(PersonEditorState.getEditorMode).subscribe(mode => {
        if (mode == 'add') {
          this.setFormDefaults();
        }
      });
      this._setupStateTaxElections();
      this.store
        .select(PersonEditorState.getEmploymentTypeId)
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(employmentTypeId => {
          // update validity - taxes tab is disabled for non-W2
          if (employmentTypeId == EmploymentTypes.W2) {
            this.taxesForm.controls.WithHoldingState.setValidators([Validators.required]);
          } else {
            this.taxesForm.controls.WithHoldingState.clearValidators();
          }
          this.taxesForm.controls.WithHoldingState.updateValueAndValidity();
        });
      this.store
        .select(PersonEditorState.isExemptFromWithholding)
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(isExempt => {
          if (isExempt) {
            this.f.FederalFilingStatus.clearValidators();
          } else {
            this.f.FederalFilingStatus.setValidators([Validators.required]);
          }
          this.f.FederalFilingStatus.updateValueAndValidity();
        });
      this.store
        .select(PersonEditorState.markFormTouched)
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(() => {
          const controls = this.taxesForm.controls;
          Object.keys(controls).forEach(key => {
            controls[key].markAsTouched();
          });
        });

      if (this.f.WithHoldingState.value) {
        this._setStateFilingStatusDefaults(this.f.WithHoldingState.value);
      }

      // determine enabling/disabling of FUTA Reason
      this.f.HasFUTAExemption.valueChanges.subscribe(flag => {
        const reason = this.f.FUTAExemptionReason;
        if (flag) {
          reason.enable();
          reason.markAsTouched();
          reason.setValidators([Validators.required]);
        } else {
          reason.disable();
          reason.setValue('');
          reason.setValidators(null);
        }

        reason.updateValueAndValidity();
      });

      // determine when to show J1F1 visa warning & enable/disable Expire Date control
      this.taxesForm.valueChanges.subscribe(form => {
        if (form.J1F1Visa == '1') {
          if (!form.HasMEDCExemption || !form.HasSSExemption) {
            this.showJ1F1VisaWarning = true;
          } else {
            this.showJ1F1VisaWarning = false;
          }

          if (!this.f.ExpireDate.enabled) {
            this.f.ExpireDate.enable();
          }
        } else if (form.J1F1Visa !== undefined) {
          this.showJ1F1VisaWarning = false;
          if (this.f.ExpireDate.value) {
            this.f.ExpireDate.setValue(null);
            this.f.ExpireDate.clearValidators();
            this.f.ExpireDate.updateValueAndValidity();
          }

          if (!this.f.ExpireDate.disabled) {
            this.f.ExpireDate.disable();
          }
        }
      });
      // VISA expiration checking
      this.f.J1F1Visa.valueChanges.subscribe(() => this.checkVisaExpiration());
      this.f.ExpireDate.valueChanges.subscribe(() => this.checkVisaExpiration());
      this.f.HasMEDCExemption.valueChanges.subscribe(() => this.checkVisaExpiration());
      this.f.HasSSExemption.valueChanges.subscribe(() => this.checkVisaExpiration());

      this.checkVisaExpiration();

      this.f.IsExemptFromWithholding.valueChanges.subscribe(() => {
        if (this.f.IsExemptFromWithholding.dirty) {
          this._onTaxExemptCheckChange();
        }
      });

      this.f.FederalFilingStatus.updateValueAndValidity();

      this.renderLocalTaxes();

      this.store.selectOnce(PersonEditorState.getReadOnly).subscribe(readOnly => {
        if (readOnly) {
          this.disableAllFormControls(this.taxesForm);
        }
      });
      if (this.isClientPortal) {
        const taxExemptionFields = [
          'ExpireDate',
          'FUTAExemptionReason',
          'HasDisLeaveExempt',
          'HasFamilyLeaveExempt',
          'HasFUTAExemption',
          'HasMEDCExemption',
          'HasMedLeaveExempt',
          'HasSSExemption',
          'HasSUIExemption',
          'J1F1Visa'
        ];

        for (let field of taxExemptionFields) {
          this.taxesForm.get(field).disable();
        }
      }
      this._linkFormToState();
    });
  }

  onCountyChange(group: UntypedFormGroup, index) {
    if (group.value.County) {
      this._focusElem(`locals-${index}-muni`);
      group.get('Municipality').enable();
    } else {
      group.get('Municipality').disable();
      group.get('SchoolDistrict').disable();
    }
  }

  onLocalTaxChange(group: UntypedFormGroup, taxCodeChanged: boolean = false) {
    this.taxesForm.markAsDirty();
    this._checkLocalTaxMatchExclude(true);

    // if the local tax config tells us that it uses State Filing Status
    // ensure null value is passed to the API for Filing Status
    if (this.getLocalConfig(group).UsesStateFilingStatus) {
      group.get('FilingStatus').setValue(null);
    } else if (group.value.TaxCode && !group.value.FilingStatus && taxCodeChanged) {
      // set any default filing statuses
      const fs = this.getLocalFilingStatuses(group);
      if (fs.length > 0) {
        const def = fs.find(x => x.IsDefault);
        if (def) {
          group.get('FilingStatus').setValue(def.Id);
        }
      }
    } else if (!group.value.TaxCode && group.value.FilingStatus && taxCodeChanged) {
      // tax code has been changed to null selection
      group.get('FilingStatus').setValue(null);
    }
  }

  onMuniChange(group: UntypedFormGroup, index) {
    if (group.value.Municipality) {
      const districts = this.getSchoolDistricts(group.value.County, group.value.Municipality);

      if (!districts || districts.length === 0) {
        // FIXME: handle this scenario after CP dev reply with solution
        console.log(
          `ERROR. ${group.value.County} ${group.value.Municipality} does not contain any school districts`
        );
      } else if (districts.length === 1) {
        // auto select district
        this._setLocalTaxCodeFromCounty(group, districts[0]);
        this.selectLocalsByResident(group, index);
      } else if (districts.length > 1) {
        group.get('SchoolDistrict').enable();
        this._focusElem(`locals-${index}-school`);
      }
    } else {
      group.get('SchoolDistrict').disable();
    }
  }

  onSchoolDistrictChange(group: UntypedFormGroup, index) {
    if (group.value.SchoolDistrict) {
      const districts = this.getSchoolDistricts(group.value.County, group.value.Municipality);
      const district = districts.find(x => x.SchoolCodeName == group.value.SchoolDistrict);
      this._setLocalTaxCodeFromCounty(group, district);
      this.selectLocalsByResident(group, index);
    }
  }

  onStateTaxElectionChange($event, value) {
    const obs: Observable<any>[] = [
      this.store.selectOnce(PersonEditorState.getPerson),
      this.store.selectOnce(PersonEditorState.getEmployeeTaxes),
      this.store.selectOnce(PersonEditorState.getEmployeeWorksIn),
      this.store.selectOnce(PersonEditorState.getEmployeeState)
    ];
    forkJoin(obs).subscribe(result => {
      const person: Person = result[0];
      const taxes: EmployeeTaxes = result[1];
      const worksIn: string = result[2];
      const residentState: string = result[3];

      const whState = $event && $event.target ? $event.target.value : value;
      const homeState = residentState || '';
      const workState = worksIn || '';

      if (whState && (!this.selWHStateId || this.selWHStateId !== whState)) {
        this.selWHStateId = whState;
        this._setTaxProfile(whState);
      } else if (!whState) {
        this.selWHStateId = '';
      }

      // set default filing statuses and validators
      this._setStateFilingStatusDefaults(whState);
      this._updateMaxAllowances();

      // make sure the selected tax election shows the tax profile data below.
      if (whState.indexOf('Both') > -1) {
        const states = this.f.WithHoldingState.value.replace('Both ', '').split(' ');
        this.getStateName(states[0]).subscribe(name => {
          this.worksInState = new Enum(states[0], name);
        });
        this.getStateName(states[1]).subscribe(name => {
          this.livesInState = new Enum(states[1], name);
        });
      } else if (whState === workState || whState === taxes.PreviousWorksInState) {
        this.getStateName(whState).subscribe(name => {
          this.worksInState = new Enum(whState, name);
        });
      } else if (whState === homeState || whState === taxes.PreviousResidentState) {
        this.getStateName(whState).subscribe(name => {
          this.livesInState = new Enum(whState, name);
        });
      }

      // US 5092
      if (person.Id && this._isPersonActive() && whState) {
        this._onActiveEmpWHStateChange(whState, workState, homeState);
      } else if (!whState) {
        // user cleared the withholding state selection
        this.f.WithHoldingState.setValidators([Validators.required]);
        this.f.WithHoldingState.updateValueAndValidity();
      }
    });
  }

  renderInvalidLocalTaxes() {
    this.store.selectOnce(PersonEditorState.getEmployeeTaxes).subscribe(taxes => {
      // if the person had local taxes selected, then changes resident or works in,
      // this panel will display those previous selections in disabled fields
      if (taxes.PreviousResidentState && taxes.Locals) {
        this.companyService
          .getLocalTaxConfigs(this.clientCode, taxes.PreviousResidentState, null)
          .subscribe(resp => {
            // filter list down from Locals set on person
            this.invalidResidentConfigs = [];
            let configs = resp || [];

            // NOTE: we are checking the configs TaxType for 'R' (resident)
            configs.forEach(config => {
              if (
                taxes.Locals.find(
                  x =>
                    x.LocalTypeId == config.LocalTypeId &&
                    config.StateAbbreviation === taxes.PreviousResidentState &&
                    config.TaxType == 'R'
                )
              ) {
                this.invalidResidentConfigs.push(config);
              }
            });
          });
      }

      if (taxes.PreviousWorksInState && taxes.Locals) {
        this.companyService
          .getLocalTaxConfigs(this.clientCode, taxes.PreviousWorksInState, null)
          .subscribe(resp => {
            // filter list down from Locals set on person
            this.invalidWorksInConfigs = [];
            let configs = resp || [];

            // NOTE: we are checking the configs TaxType for 'N' (non-resident)
            configs.forEach(config => {
              if (
                taxes.Locals.find(
                  x =>
                    x.LocalTypeId == config.LocalTypeId &&
                    config.StateAbbreviation === taxes.PreviousWorksInState &&
                    config.TaxType == 'N'
                )
              ) {
                this.invalidWorksInConfigs.push(config);
              }
            });
          });
      }
    });
  }

  renderLocalTaxes() {
    this.store.selectOnce(PersonEditorState.getEmployeeState).subscribe(residentState => {
      this.store.selectOnce(PersonEditorState.getEmployeeWorksIn).subscribe(worksInState => {
        const work = worksInState;
        const resident = residentState;
        this.companyService.getLocalTaxConfigs(this.clientCode, resident, work).subscribe(resp => {
          this.localTaxConfigs = resp || [];
          this._setLocalTaxValidation();
          this._setLocalTaxStateNameAndUrls();
          this._checkLocalTaxMatchExclude(false);

          // setup locals
          const countySelect = this.localTaxConfigs.find(
            x => x.StateAbbreviation == resident && x.IsSelectByCountyAvailable
          );

          if (this.selResidentState != resident && countySelect) {
            this.selResidentState = resident;
            this.adminService.getLocalTaxesByCounty(resident).subscribe(localsByCounty => {
              this.localsByCounty = localsByCounty;
            });
          }
        });

        this.renderInvalidLocalTaxes();
      });
    });
  }

  selectLocalsByCounty(group: UntypedFormGroup, index) {
    group.get('SchoolDistrict').setValue('', { emitEvent: false });
    group.get('Municipality').setValue('', { emitEvent: false });
    group.get('County').setValue('', { emitEvent: false });

    group.get('SchoolDistrict').disable();
    group.get('Municipality').disable();

    // if the underlying tax code is required,
    // set county/muni/school as required when visibile
    const localTypeId = group.get('LocalTypeId').value;
    const config = this.localTaxConfigs.find(x => x.LocalTypeId == localTypeId);
    if (config.IsRequired) {
      group.get('SchoolDistrict').setValidators([Validators.required]);
      group.get('Municipality').setValidators([Validators.required]);
      group.get('County').setValidators([Validators.required]);
      this._updateCountyMuniCtrls(group);
    }

    this.taxesForm.markAsDirty();
    this.selectByCounty[index] = true; // show county controls
    this._focusElem(`locals-${index}-county`);
  }

  selectLocalsByResident(group: UntypedFormGroup, index) {
    this.selectByCounty[index] = false; // hide county controls
    this._focusElem(`locals-${index}-tax-code`);

    // clear required validators on county/muni incase they were set
    group.get('County').clearValidators();
    group.get('Municipality').clearValidators();
    group.get('SchoolDistrict').clearValidators();
    this._updateCountyMuniCtrls(group);
  }

  setFormDefaults() {
    this._getDefaultFederalFilingStatus()
      .pipe(take(1))
      .subscribe(federalFilingStatus => {
        // fed filing status
        if (!this.f.FederalFilingStatus.value && !this.f.IsExemptFromWithholding.value) {
          this.f.FederalFilingStatus.setValue(federalFilingStatus);
        } else if (this.f.FederalFilingStatus.value && this.f.IsExemptFromWithholding.value) {
          this.f.FederalFilingStatus.setValue('');
        }

        // fed wh allowance
        if (
          this.f.IsExemptFromWithholding.value &&
          (this.f.FederalWithholdingAllowance.value ||
            this.f.FederalWithholdingAllowance.value === 0)
        ) {
          this.f.FederalWithholdingAllowance.setValue('');
        } else if (
          !this.f.IsExemptFromWithholding.value &&
          !this.f.FederalWithholdingAllowance.value &&
          this.f.FederalWithholdingAllowance.value != 0
        ) {
          this.f.FederalWithholdingAllowance.setValue(0);
        }

        if (
          this.f.IsExemptFromWithholding.value &&
          (this.f.ChildTaxCredit.value || this.f.ChildTaxCredit.value === 0)
        ) {
          this.f.ChildTaxCredit.setValue('');
        }

        if (
          this.f.IsExemptFromWithholding.value &&
          (this.f.OtherIncome.value || this.f.OtherIncome.value === 0)
        ) {
          this.f.OtherIncome.setValue('');
        }

        if (
          this.f.IsExemptFromWithholding.value &&
          (this.f.WageDeduction.value || this.f.WageDeduction.value === 0)
        ) {
          this.f.WageDeduction.setValue('');
        }

        if (
          !this.f.StateWithholdingAllowance.value &&
          this.f.StateWithholdingAllowance.value != 0
        ) {
          this.f.StateWithholdingAllowance.setValue(0);
        }

        // enable/disable FUTA reason
        if (this.f.HasFUTAExemption.value) {
          this.f.FUTAExemptionReason.enable();
        } else {
          this.f.FUTAExemptionReason.disable();
        }

        // J1F1Visa expire date enable/disable
        if (this.f.J1F1Visa.value && this.f.J1F1Visa.value == '1') {
          this.f.ExpireDate.enable();
        } else {
          this.f.ExpireDate.disable();
          if (this.f.ExpireDate.value) {
            this.f.ExpireDate.setValue(null);
          }
        }
        this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
          if (!person.Id && !this.f.J1F1Visa.value && this.f.J1F1Visa.value != 0) {
            this.f.J1F1Visa.setValue('0');
          }
        });

        if (this.f.W4Version.value == null || this.f.W4Version.value == undefined) {
          // if current date >= Jan 1, 2020 - default to '2020 or later' option
          // otherwise, default to 'pre-2020' option
          if (moment(new Date()).isSameOrAfter(new Date(2020, 0, 1), 'day')) {
            this.f.W4Version.setValue('1');
          } else {
            this.f.W4Version.setValue('0');
          }
        }
        this._setupStateTaxElections();
      });
  }

  public showAddlTaxExemption(exemption: string): Observable<boolean> {
    return this._getStateTaxExemptions().pipe(
      map(addl => {
        return addl ? Object.keys(addl.EmployeeStateTaxExemptions).includes(exemption) : null;
      })
    );
  }

  // #region PRIVATE METHODS

  // some local tax config combinations are not allowed
  // config.ExcludeFrom - id of other config that CANNOT be selected with this
  // config.MatchesOn - id of other config that MUST be selected with this
  private _checkLocalTaxMatchExclude(clearValidation: boolean) {
    if (clearValidation) {
      this._setLocalTaxValidation();
    }

    // if there are values seleted for local taxes and we have configs
    const locals = this.taxesForm.controls.Locals as UntypedFormArray;

    Object.keys(locals.controls).forEach(key => {
      const local = locals.controls[key] as UntypedFormGroup;
      // for each selected tax code
      if (local.get('TaxCode').value) {
        // fetch config
        const id = local.get('LocalTypeId').value;
        const config = this.localTaxConfigs.find(x => x.LocalTypeId == id);

        const match = this._getLocalTax(config.MatchesOn);
        const exclude = this._getLocalTax(config.ExcludeFrom);

        if (config.MatchesOn && match && !match.get('TaxCode').value) {
          // validation error. match tax code has not been selected
          const matchConfig = this.localTaxConfigs.find(
            x => match.value.LocalTypeId == x.LocalTypeId
          );

          match.controls.TaxCode.markAsTouched();
          match.controls.TaxCode.setErrors({
            matchesOn: {
              message: `This tax agency must be selected with ${config.TaxLabel}: ${
                local.get('TaxCode').value
              }`
            }
          });

          this.taxesForm.updateValueAndValidity();
        }

        if (config.ExcludeFrom && exclude && exclude.get('TaxCode').value) {
          // validation error. exclude tax code cannot be selected
          const excludeConfig = this.localTaxConfigs.find(
            x => exclude.value.LocalTypeId == x.LocalTypeId
          );

          exclude.controls.TaxCode.setErrors({
            excludeFrom: {
              message: `This tax agency cannot be selected with ${config.TaxLabel}: ${
                local.get('TaxCode').value
              }`
            }
          });

          local.controls.TaxCode.setErrors({
            excludeFrom: {
              message: `This tax agency cannot be selected with ${excludeConfig.TaxLabel}: ${
                exclude.get('TaxCode').value
              } `
            }
          });

          this.taxesForm.updateValueAndValidity();
        }
      }
    });
  }

  private _focusElem(elemId: string) {
    setTimeout(() => {
      const elem = document.getElementById(elemId);
      if (elem) {
        elem.focus();
      }
    }, 100);
  }

  private _getDefaultFederalFilingStatus(): Observable<string> {
    return this.getFederalFilingStatuses().pipe(
      map(filingStatuses => {
        const def = filingStatuses.find(x => x.IsDefault);
        if (def) {
          return def.Id;
        } else {
          return '';
        }
      })
    );
  }

  private _getDefaultStateFilingStatus(stateAbbrev: string): Observable<string> {
    return this.getStateIncomeFilingStatuses(stateAbbrev).pipe(
      map(filingStatuses => {
        const def = filingStatuses.find(x => x.IsDefault);
        if (def) {
          return def.Id;
        } else {
          return '';
        }
      })
    );
  }

  private _getStateTaxExemptions(): Observable<StateTaxExemption> {
    return this.store.selectOnce(PersonEditorState.getEmployeeTaxExemptions).pipe(
      mergeMap(taxExemptions => {
        return this.store.selectOnce(PersonEditorState.getEmployeeWorksIn).pipe(
          map(worksIn => {
            return {
              taxExemptions,
              worksIn
            };
          })
        );
      }),
      map(data => {
        const worksIn = data.worksIn;

        if (!worksIn || !worksIn || !data.taxExemptions) {
          return null;
        }

        return data.taxExemptions.EmployeeStateTaxExemptionsByState?.find(
          x => x.EmployeeTaxExemptionState == worksIn
        );
      })
    );
  }

  private _getLocalTax(localTypeId): UntypedFormGroup {
    let fg;
    const locals = this.taxesForm.controls.Locals as UntypedFormArray;

    Object.keys(locals.controls).forEach(key => {
      const local = locals.controls[key] as UntypedFormGroup;
      if (local.get('LocalTypeId').value == localTypeId) {
        fg = local;
      }
    });

    return fg;
  }

  private _isPersonActive(): Observable<boolean> {
    return this.store.selectOnce(PersonEditorState.getEmployeeStatuses).pipe(
      map(statuses => {
        return statuses[0] && statuses[0].StatusId == 0; /*active*/
      })
    );
  }

  private _handleInvalidTaxSetup() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      this.store
        .selectOnce(PersonEditorState.getCompanyTaxConfigs)
        .subscribe(companyTaxCodeConfigs => {
          this.store.selectOnce(PersonEditorState.getEmployeeTaxes).subscribe(taxes => {
            this.store.selectOnce(PersonEditorState.getEmployeeWorksIn).subscribe(worksIn => {
              this.store.selectOnce(PersonEditorState.getEmployeeState).subscribe(homeState => {
                // NOTE: if a person has setup tax election(s) and the work/resident state is changed
                // the general practice on the taxes tab is to leave the old selections available
                // present error under the field and if person's status is Active, force change to valid WH state before save
                // see US 5092, BUG 6165 for more info
                const elections = taxes.FedAndState.filter(x => {
                  const config = companyTaxCodeConfigs.find(c => c.TaxCode === x.TaxCode);
                  return x.IsActive && config && config.EntityType === 'S';
                });

                const whState = this.f.WithHoldingState.value;
                let errors = false;

                // person previously had 'Both' selected and then changed resident or work state
                if (elections.length > 1 && taxes.PreviousResidentState != null) {
                  // determine which of the elections were work & resident
                  // look at previously selected and currently selected values
                  let w = taxes.PreviousWorksInState;
                  let r = taxes.PreviousResidentState;

                  elections.forEach(x => {
                    if (!w && x.StateAbbreviation === worksIn) {
                      w = x.StateAbbreviation;
                    }

                    if (!r && x.StateAbbreviation === homeState) {
                      r = x.StateAbbreviation;
                    }
                  });
                  const obs = [this.getStateName(w), this.getStateName(r)];
                  forkJoin(obs).subscribe(states => {
                    this.bothWHStateOpts.push({
                      key: `Both ${w} ${r}`,
                      value: `Both ${states[0]} and ${states[1]}`
                    });
                    this.f.WithHoldingState.setValue(`Both ${w} ${r}`);
                    this.onStateTaxElectionChange(null, this.f.WithHoldingState.value);
                  });
                } else if (whState && this.withHoldingStateIds.indexOf(whState) === -1) {
                  // if 'Both' was previously selected, check to see if states match
                  const selectedState = this.f.WithHoldingState.value;
                  if (selectedState.indexOf('Both') > -1) {
                    const stateAbbrevs = this.f.WithHoldingState.value
                      .replace('Both ', '')
                      .split(' '); // ['OH', 'PA']

                    // see if the 'Both' selection matches what the person's has currently selected for work/resident state
                    if (
                      stateAbbrevs.indexOf(worksIn) === -1 ||
                      stateAbbrevs.indexOf(homeState) === -1
                    ) {
                      // one or both of the previously selected states has changed
                      errors = true;
                    }
                  } else {
                    this.withHoldingStateIds.push(this.f.WithHoldingState.value);
                    errors = true;
                  }

                  this.onStateTaxElectionChange(null, this.f.WithHoldingState.value);
                }

                if (
                  this._isPersonActive() &&
                  (errors || person.InvalidToPayDetails?.find(x => x.FieldName == 'Taxes'))
                ) {
                  this.invalidTaxMessage = 'Tax elections may be incorrect';
                }
              });
            });
          });
        });
    });
  }

  private _loadHomeStateTaxProfileData(tax: EmployeeTax) {
    // check to see if value is the same before setting it
    // to avoid unnecessary change detection logic from firing
    const addlWhType =
      tax.AdditionalWithholdingType && tax.AdditionalWithholdingType.trim()
        ? tax.AdditionalWithholdingType.trim()
        : 'null';

    if (this.f.AddlStateFilingStatus.value != tax.FilingStatus) {
      this.f.AddlStateFilingStatus.setValue(tax.FilingStatus);
    }

    if (
      this.f.AddlStateWithholdingAllowance.value != tax.WithholdingAllowance &&
      this.f.AddlStateWithholdingAllowance.value != 0
    ) {
      this.f.AddlStateWithholdingAllowance.setValue(tax.WithholdingAllowance || 0);
    }

    if (
      this.f.AddlStateAddlAllowances.value != tax.AdditionalAllowances &&
      this.f.AddlStateAddlAllowances.value != 0
    ) {
      this.f.AddlStateAddlAllowances.setValue(tax.AdditionalAllowances || 0);
    }

    if (this.f.AddlStateAddlWithholdingType.value != addlWhType) {
      this.f.AddlStateAddlWithholdingType.setValue(
        addlWhType && addlWhType.trim() ? addlWhType.trim() : 'null'
      );
    }

    if (
      this.f.AddlStateAddlWithholdingValue.value != tax.AdditionalWithholdingAmount &&
      this.f.AddlStateAddlWithholdingValue.value != 0
    ) {
      this.f.AddlStateAddlWithholdingValue.setValue(tax.AdditionalWithholdingAmount || 0);
    }
  }

  private _loadWorkStateTaxProfileData(tax: EmployeeTax) {
    // check to see if value is the same before setting it
    // to avoid unnecessary change detection logic from firing
    const addlWhType =
      tax.AdditionalWithholdingType && tax.AdditionalWithholdingType.trim()
        ? tax.AdditionalWithholdingType.trim()
        : 'null';

    if (this.f.StateFilingStatus.value !== tax.FilingStatus) {
      this.f.StateFilingStatus.setValue(tax.FilingStatus);
    }

    if (
      this.f.StateWithholdingAllowance.value != tax.WithholdingAllowance &&
      this.f.StateWithholdingAllowance.value != 0
    ) {
      this.f.StateWithholdingAllowance.setValue(tax.WithholdingAllowance || 0);
    }

    if (
      this.f.StateAddlAllowances.value != tax.AdditionalAllowances &&
      this.f.StateAddlAllowances.value != 0
    ) {
      this.f.StateAddlAllowances.setValue(tax.AdditionalAllowances || 0);
    }

    if (this.f.StateAddlWithholdingType.value != addlWhType) {
      this.f.StateAddlWithholdingType.setValue(addlWhType);
    }

    if (
      this.f.StateAddlWithholdingValue.value != tax.AdditionalWithholdingAmount &&
      this.f.StateAddlWithholdingValue.value != 0
    ) {
      this.f.StateAddlWithholdingValue.setValue(tax.AdditionalWithholdingAmount || 0);
    }
  }

  private _onActiveEmpWHStateChange(whState, workState, homeState) {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      const invalidTaxes = person.InvalidToPayDetails?.find(x => x.FieldName == 'Taxes');

      if (whState.indexOf('Both') > -1 && invalidTaxes) {
        const stateAbbrevs = this.f.WithHoldingState.value.replace('Both ', '').split(' '); // ['OH', 'PA']

        // see if the 'Both' selection matches what the person's has currently selected for work/resident state
        if (stateAbbrevs.indexOf(workState) === -1 || stateAbbrevs.indexOf(homeState) === -1) {
          // one or both of the previously selected states has changed
          this.invalidTaxMessage = 'Tax elections may be incorrect';
        }
      } else if (whState.indexOf('Both') === -1 && invalidTaxes) {
        this.invalidTaxMessage = 'Tax elections may be incorrect';
      } else if (!invalidTaxes) {
        this.invalidTaxMessage = null;
      }
    });
  }

  private _onTaxExemptCheckChange() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      this.f.IsExemptFromWithholding.markAsTouched();
      if (
        person.Id &&
        this.f.IsExemptFromWithholding.value &&
        !this.ngbModalService.hasOpenModals()
      ) {
        // prompt user that values will get cleared
        this.modalService.showConfirmModal(
          `Marking this person as exempt from withholding will clear current tax selections. Are you sure you want to mark ${person.FirstName} ${person.LastName} as exempt from federal taxes?`,
          'Do not mark exempt',
          `Mark Exempt`
        );
        this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
          if (!confirm) {
            this.f.IsExemptFromWithholding.setValue(false, {
              emitEvent: false
            });
          }

          this._updateTaxExemption();
        });
      } else {
        this._updateTaxExemption();
      }
    });
  }

  private _resetFederalTaxElections() {
    this._getDefaultFederalFilingStatus()
      .pipe(take(1))
      .subscribe(federalFilingStatus => {
        if (!this.f.IsExemptFromWithholding.value) {
          this.f.FederalAddlWithholdingType.setValue('null');
          this.f.FederalWithholdingAllowance.setValue(0);
          this.f.FederalFilingStatus.setValue(federalFilingStatus);
          this.f.IsHigherWithholding.setValue(false);
          this.f.ChildTaxCredit.setValue(0);
          this.f.OtherIncome.setValue(0);
          this.f.WageDeduction.setValue(0);
        }
      });
  }

  private _setLocalTaxCodeFromCounty(group: UntypedFormGroup, district: SchoolDistrict) {
    // if the selected tax code was not previously available
    // add it to the list
    const config = this.localTaxConfigs.find(x => x.LocalTypeId == group.value.LocalTypeId);
    if (!config.TaxCodes.find(x => x.Key == district.ResidentTaxCode)) {
      config.TaxCodes.push(new KeyValuePair(district.ResidentTaxCode, district.ResidentTaxName));
    }

    group.get('TaxCode').setValue(district.ResidentTaxCode);
    group.get('TaxCode').updateValueAndValidity();
  }

  private _setLocalTaxStateNameAndUrls() {
    this.localTaxStateNames = [];
    this.localTaxStateUrls = [];

    this.localTaxConfigs.forEach(x => {
      if (x.StateURL) {
        this.getStateName(x.StateAbbreviation).subscribe(name => {
          if (this.localTaxStateNames.indexOf(name) === -1) {
            this.localTaxStateNames.push(name);
            this.localTaxStateUrls.push(x.StateURL);
          }
        });
      }
    });
  }

  private _setLocalTaxValidation() {
    this.requiredLocalTaxText = '';
    const reqStates = [];

    // loop over formArray/formGroup
    const fa = this.taxesForm.get('Locals') as UntypedFormArray;
    fa.controls.forEach((fg: UntypedFormGroup) => {
      // first clear all validators
      Object.keys(fg.controls).forEach(ctrl => {
        fg.controls[ctrl].clearValidators();
      });

      const config = this.localTaxConfigs.find(x => x.LocalTypeId == fg.value.LocalTypeId);
      if (config && config.IsRequired) {
        fg.controls.TaxCode.setValidators(Validators.required);
        fg.controls.WithholdingAllowances.setValidators(Validators.required);

        if (config.FilingStatuses && config.FilingStatuses.length > 0) {
          fg.controls.FilingStatus.setValidators(Validators.required);
        }

        if (reqStates.indexOf(config.StateAbbreviation) == -1) {
          reqStates.push(config.StateAbbreviation);
        }
      }

      this.store.selectOnce(PersonEditorState.getReadOnly).subscribe(readOnly => {
        Object.keys(fg.controls).forEach(ctrl => {
          fg.controls[ctrl].updateValueAndValidity();
          if (readOnly) {
            fg.controls[ctrl].disable();
          }
        });
      });
    });

    if (reqStates.length > 0) {
      let stateNamesObs = reqStates.map(x => this.getStateName(x));
      forkJoin(stateNamesObs).subscribe(stateNames => {
        this.requiredLocalTaxText = `Required for ${stateNames.join(' and ')}`;
      });
    }

    this.taxesForm.updateValueAndValidity();
  }

  private _setStateFilingStatusDefaults(val) {
    this.store.selectOnce(PersonEditorState.getEmployeeState).subscribe(residentState => {
      this.store.selectOnce(PersonEditorState.getEmployeeWorksIn).subscribe(worksIn => {
        // if either of the filing status fields are displayed
        // make them required and set default values if available
        const homeState = residentState || '';
        const hasHomeStateConfig = this.getStateIncomeTaxConfig(homeState) !== null;

        const obs = [
          this.isStateIncomeWithHolding(worksIn),
          this.isStateIncomeWithHolding(homeState)
        ];

        forkJoin(obs).subscribe(([worksInWithholding, homeStateWithholding]) => {
          if (
            val &&
            worksIn &&
            (val.indexOf('Both') > -1 || val === worksIn) &&
            worksInWithholding
          ) {
            if (!this.f.StateFilingStatus.value) {
              this._getDefaultStateFilingStatus(worksIn).subscribe(s => {
                this.f.StateFilingStatus.setValue(s);
              });
            }

            this.f.StateFilingStatus.setValidators([Validators.required]);
          } else {
            this.f.StateFilingStatus.clearValidators();
          }

          if (
            val &&
            homeState &&
            hasHomeStateConfig &&
            (val.indexOf('Both') > -1 || val === homeState) &&
            homeState !== worksIn &&
            homeStateWithholding
          ) {
            if (!this.f.AddlStateFilingStatus.value) {
              this._getDefaultStateFilingStatus(homeState).subscribe(s => {
                this.f.AddlStateFilingStatus.setValue(s);
              });
            }

            this.f.AddlStateFilingStatus.setValidators([Validators.required]);
          } else {
            this.f.AddlStateFilingStatus.clearValidators();
          }

          this.f.StateFilingStatus.updateValueAndValidity();
          this.f.AddlStateFilingStatus.updateValueAndValidity();

          if (
            val.indexOf('Both') > -1 &&
            !this.f.AddlStateWithholdingAllowance.value &&
            this.f.AddlStateWithholdingAllowance.value != 0
          ) {
            this.f.AddlStateWithholdingAllowance.setValue(0);
          }
        });
      });
    });
  }

  private _setTaxProfile(whStateSelection: string) {
    this.store.selectOnce(PersonEditorState.getEmployeeTaxes).subscribe(taxes => {
      this.store.selectOnce(PersonEditorState.getEmployeeState).subscribe(residentState => {
        this.store.selectOnce(PersonEditorState.getEmployeeWorksIn).subscribe(worksIn => {
          let workConfig;
          let workState;
          let homeConfig;
          let homeState;

          if (whStateSelection.indexOf('Both') > -1) {
            const states = whStateSelection.replace('Both ', '').split(' ');
            workConfig = this.getStateIncomeTaxConfig(states[0]);
            homeConfig = this.getStateIncomeTaxConfig(states[1]);
            workState = states[0];
            homeState = states[1];
          } else {
            if (whStateSelection === worksIn || whStateSelection === taxes.PreviousWorksInState) {
              workConfig = this.getStateIncomeTaxConfig(whStateSelection);
              workState = whStateSelection;
            }

            if (
              whStateSelection === residentState ||
              whStateSelection === taxes.PreviousResidentState
            ) {
              homeConfig = this.getStateIncomeTaxConfig(whStateSelection);
              homeState = whStateSelection;
            }
          }

          // load any found tax profiles
          if (workConfig) {
            const workTax = taxes.FedAndState.find(x => x.TaxCode === workConfig.TaxCode);

            if (workTax) {
              this._loadWorkStateTaxProfileData(workTax);
            } else {
              this._loadWorkStateTaxProfileData(new EmployeeTax());
              this._setStateFilingStatusDefaults(workState);
            }
          }

          if (homeConfig) {
            const homeTax = taxes.FedAndState.find(x => x.TaxCode === homeConfig.TaxCode);

            if (homeTax) {
              this._loadHomeStateTaxProfileData(homeTax);
            } else {
              this._loadHomeStateTaxProfileData(new EmployeeTax());
              this._setStateFilingStatusDefaults(homeState);
            }
          }
        });
      });
    });
  }

  // on page init, set default tax election values and UI state
  private _setupStateTaxElections() {
    this.store.selectOnce(PersonEditorState.getEmployeeState).subscribe(homeState => {
      this.store.selectOnce(PersonEditorState.getEmployeeWorksIn).subscribe(worksIn => {
        this.bothWHStateOpts = [];

        const obs = [
          this.getStateIncomeTaxConfig(worksIn),
          this.getStateIncomeTaxConfig(homeState),
          this.getStateName(homeState),
          this.getStateName(worksIn)
        ];

        forkJoin(obs).subscribe((res: any[]) => {
          this.livesInState = new Enum(homeState, res[2]);
          this.worksInState = new Enum(worksIn, res[3]);
          const worksInName = res[3];
          const livesInName = res[2];
          const workStateTaxconfig = res[0];
          const homeStateTaxConfig = res[1];
          if (worksIn && this.withHoldingStateIds.indexOf(worksIn) === -1) {
            this.withHoldingStateIds.push(worksIn);
            this.f.WithHoldingState.enable();

            // set default filing statuses to first in list
            if (!this.f.StateFilingStatus.value && worksIn) {
              this._getDefaultStateFilingStatus(worksIn).subscribe(s => {
                this.f.StateFilingStatus.setValue(s);
              });
            }

            if (!this.f.AddlStateFilingStatus.value && homeState.trim() && homeStateTaxConfig) {
              this._getDefaultStateFilingStatus(homeState).subscribe(s => {
                this.f.AddlStateFilingStatus.setValue(s);
              });
            }
          } else if (!worksIn) {
            // works in state is required in order to enter tax elections
            this.f.WithHoldingState.disable();
          }

          // Bug 4987 - do not show home state if there is not a config for it
          if (homeStateTaxConfig && this.withHoldingStateIds.indexOf(homeState) === -1) {
            this.withHoldingStateIds.push(homeState);
          }

          this._handleInvalidTaxSetup();

          // show 'Both' option if both state tax configs are income withholding
          if (
            workStateTaxconfig &&
            homeStateTaxConfig &&
            workStateTaxconfig.IsIncomeWithholdingState &&
            homeStateTaxConfig.IsIncomeWithholdingState &&
            worksIn != homeState
          ) {
            this.bothWHStateOpts.push({
              key: `Both ${worksIn} ${homeState}`,
              value: `Both ${worksInName} and ${livesInName}`
            });
          }

          // if work state has been set, it is the only option, and an income withholding state, set it as the default selection
          if (
            worksIn &&
            !this.f.WithHoldingState.value &&
            this.bothWHStateOpts.length === 0 &&
            ((workStateTaxconfig && workStateTaxconfig.IsIncomeWithholdingState) ||
              this.withHoldingStateIds.length === 1)
          ) {
            setTimeout(() => {
              this.f.WithHoldingState.setValue(worksIn);
              this.onStateTaxElectionChange(null, worksIn);
              this._setStateFilingStatusDefaults(worksIn);
              this._getDefaultStateFilingStatus(worksIn).subscribe(s => {
                this.f.StateFilingStatus.setValue(s);
              });
            }, 0);
          }

          this.selWHStateId = this.f.WithHoldingState.value || '';

          this._updateFederalWHValidation();
          this.f.FederalAddlWithholdingType.valueChanges.subscribe(() =>
            this._updateFederalWHValidation()
          );

          this._updateStateWHValidation();
          this.f.StateAddlWithholdingType.valueChanges.subscribe(() =>
            this._updateStateWHValidation()
          );

          this._updateAddlStateWHValidation();
          this.f.AddlStateAddlWithholdingType.valueChanges.subscribe(
            () => this._updateAddlStateWHValidation
          );
          this.f.W4Version.valueChanges.subscribe(() => {
            if (this.f.W4Version.dirty) {
              this._resetFederalTaxElections();
            }
          });

          this._updateMaxAllowances();
        });
      });
    });
  }

  private _updateAddlStateWHValidation() {
    const value = this.f.AddlStateAddlWithholdingType.value;
    if (value == 'P') {
      this.f.AddlStateAddlWithholdingValue.setValidators([
        Validators.required,
        Validators.max(100.0)
      ]);
    } else if (value && value !== 'null') {
      this.f.AddlStateAddlWithholdingValue.setValidators([
        Validators.required,
        Validators.max(9999999.99)
      ]);
    } else {
      this.f.AddlStateAddlWithholdingValue.setValue(0, { emitEvent: false });
      this.f.AddlStateAddlWithholdingValue.clearValidators();
    }

    this.f.AddlStateAddlWithholdingValue.updateValueAndValidity();
  }

  private _updateCountyMuniCtrls(group: UntypedFormGroup) {
    group.get('County').updateValueAndValidity();
    group.get('Municipality').updateValueAndValidity();
    group.get('SchoolDistrict').updateValueAndValidity();
  }

  private _updateFederalWHValidation() {
    const value = this.f.FederalAddlWithholdingType.value;
    if (value == 'P') {
      this.f.FederalAddlWithholdingValue.setValidators([
        Validators.required,
        Validators.max(100.0)
      ]);
    } else if (value && value !== 'null') {
      this.f.FederalAddlWithholdingValue.setValidators([
        Validators.required,
        Validators.max(9999999.99)
      ]);
    } else {
      if (this.f.FederalAddlWithholdingValue.value != 0) {
        this.f.FederalAddlWithholdingValue.setValue(0);
      }

      this.f.FederalAddlWithholdingValue.clearValidators();
    }

    this.f.FederalAddlWithholdingValue.updateValueAndValidity();
  }

  private _updateMaxAllowances() {
    this.f.StateAddlAllowances.clearValidators();
    this.f.AddlStateAddlAllowances.clearValidators();
    this.f.StateWithholdingAllowance.clearValidators();
    this.f.AddlStateWithholdingAllowance.clearValidators();

    if (
      this.selWHStateId &&
      (this.selWHStateId === this.worksInState.Id || this.selWHStateId.indexOf('Both') > -1)
    ) {
      // set work state max allowance/addl allowance
      this.getStateIncomeTaxConfig(this.worksInState.Id).subscribe(config => {
        if (config && config.MaxAllowedAllowances) {
          this.f.StateWithholdingAllowance.setValidators([
            Validators.max(config.MaxAllowedAllowances)
          ]);
        }

        if (config && config.MaxAllowedAddlAllowances) {
          this.f.StateAddlAllowances.setValidators([
            Validators.max(config.MaxAllowedAddlAllowances)
          ]);
        }
      });
    }

    if (
      this.selWHStateId &&
      (this.selWHStateId === this.livesInState.Id || this.selWHStateId.indexOf('Both') > -1)
    ) {
      // set home state max allowance/addl allowance
      const config = this.getStateIncomeTaxConfig(this.livesInState.Id);
      this.getStateIncomeTaxConfig(this.livesInState.Id).subscribe(config => {
        if (config && config.MaxAllowedAllowances) {
          this.f.AddlStateWithholdingAllowance.setValidators([
            Validators.max(config.MaxAllowedAllowances)
          ]);
        }

        if (config && config.MaxAllowedAddlAllowances) {
          this.f.AddlStateAddlAllowances.setValidators([
            Validators.max(config.MaxAllowedAddlAllowances)
          ]);
        }
      });
    }

    this.f.StateAddlAllowances.updateValueAndValidity();
    this.f.AddlStateAddlAllowances.updateValueAndValidity();
    this.f.StateWithholdingAllowance.updateValueAndValidity();
    this.f.AddlStateWithholdingAllowance.updateValueAndValidity();
  }

  private _updateStateWHValidation() {
    const value = this.f.StateAddlWithholdingType.value;
    if (value == 'P') {
      this.f.StateAddlWithholdingValue.setValidators([Validators.required, Validators.max(100.0)]);
    } else if (value && value !== 'null') {
      this.f.StateAddlWithholdingValue.setValidators([
        Validators.required,
        Validators.max(9999999.99)
      ]);
    } else {
      this.f.StateAddlWithholdingValue.setValue(0);
      this.f.StateAddlWithholdingValue.clearValidators();
    }

    this.f.StateAddlWithholdingValue.updateValueAndValidity();
  }

  private _updateTaxExemption() {
    this._getDefaultFederalFilingStatus()
      .pipe(take(1))
      .subscribe(federalFilingStatus => {
        if (this.f.IsExemptFromWithholding.value) {
          this.f.FederalFilingStatus.clearValidators();
          this.f.FederalFilingStatus.setValue('');
          this.f.FederalWithholdingAllowance.setValue('');
          this.f.FederalAddlWithholdingType.setValue('null', { emitEvent: false });

          this.f.IsHigherWithholding.setValue(false);
          this.f.ChildTaxCredit.setValue('');
          this.f.OtherIncome.setValue('');
          this.f.WageDeduction.setValue('');
        } else {
          this.f.FederalFilingStatus.setValue(federalFilingStatus);
          this.f.FederalWithholdingAllowance.setValue(0);
          this.f.FederalFilingStatus.setValidators([Validators.required]);
          this.f.ChildTaxCredit.setValue(0);
          this.f.OtherIncome.setValue(0);
          this.f.WageDeduction.setValue(0);
        }

        this.f.FederalFilingStatus.updateValueAndValidity();
      });
  }

  private _linkFormToState() {
    this.taxesForm
      .get('FederalFilingStatus')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetFederalFilingStatus(val));
      });
    this.store
      .select(TaxesFormSelectors.federalFilingStatus)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('FederalFilingStatus');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('FederalWithholdingAllowance')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetFederalWithholdingAllowance(val));
      });
    this.store
      .select(TaxesFormSelectors.federalWithholdingAllowance)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('FederalWithholdingAllowance');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('FederalAddlWithholdingType')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetFederalAddlWithholdingType(val));
      });
    this.store
      .select(TaxesFormSelectors.federalAddlWithholdingType)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('FederalAddlWithholdingType');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('FederalAddlWithholdingValue')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetFederalAddlWithholdingValue(val));
      });
    this.store
      .select(TaxesFormSelectors.federalAddlWithholdingValue)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('FederalAddlWithholdingValue');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('HasSSExemption')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHasSSExemption(val));
      });
    this.store
      .select(TaxesFormSelectors.hasSSExemption)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('HasSSExemption');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('HasMEDCExemption')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHasMEDCExemption(val));
      });
    this.store
      .select(TaxesFormSelectors.hasMEDCExcemption)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('HasMEDCExemption');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('HasSUIExemption')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHasSUIExemption(val));
      });
    this.store
      .select(TaxesFormSelectors.hasSUIExemption)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('HasSUIExemption');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('HasFUTAExemption')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHasFUTAExemption(val));
      });
    this.store
      .select(TaxesFormSelectors.hasFUTAExemption)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('HasFUTAExemption');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('HasDisLeaveExempt')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHasDisLeaveExempt(val));
      });
    this.store
      .select(TaxesFormSelectors.hasDisLeaveExempt)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('HasDisLeaveExempt');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('HasFamilyLeaveExempt')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHasFamilyLeaveExempt(val));
      });
    this.store
      .select(TaxesFormSelectors.hasFamilyLeaveExempt)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('HasFamilyLeaveExempt');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('HasMedLeaveExempt')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetHasMedLeaveExempt(val));
      });
    this.store
      .select(TaxesFormSelectors.hasMedLeaveExempt)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('HasMedLeaveExempt');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('FUTAExemptionReason')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetFUTAExemptionReason(val));
      });
    this.store
      .select(TaxesFormSelectors.FUTAExemptionReason)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('FUTAExemptionReason');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('J1F1Visa')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetJ1F1Visa(val));
      });
    this.store
      .select(TaxesFormSelectors.J1F1Visa)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('J1F1Visa');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('ExpireDate')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetExpireDate(val));
      });
    this.store
      .select(TaxesFormSelectors.expireDate)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('ExpireDate');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('WithHoldingState')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetWithHoldingState(val));
      });
    this.store
      .select(TaxesFormSelectors.withholdingState)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('WithHoldingState');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
          this.onStateTaxElectionChange(null, val);
        }
      });
    this.taxesForm
      .get('StateFilingStatus')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetStateFilingStatus(val));
      });
    this.store
      .select(TaxesFormSelectors.stateFilingStatus)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('StateFilingStatus');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('StateWithholdingAllowance')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetStateWithholdingAllowance(val));
      });
    this.store
      .select(TaxesFormSelectors.stateWithholdingAllowance)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('StateWithholdingAllowance');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('StateAddlAllowances')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetStateAddlAllowances(val));
      });
    this.store
      .select(TaxesFormSelectors.stateAddlAllowances)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('StateAddlAllowances');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('StateAddlWithholdingType')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetStateAddlWithholdingType(val));
      });
    this.store
      .select(TaxesFormSelectors.stateAddlWithholdingType)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('StateAddlWithholdingType');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('StateAddlWithholdingValue')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetStateAddlWithholdingValue(val));
      });
    this.store
      .select(TaxesFormSelectors.stateAddlWithholdingValue)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('StateAddlWithholdingValue');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('AddlStateFilingStatus')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetAddlStateFilingStatus(val));
      });
    this.store
      .select(TaxesFormSelectors.addlStateFilingStatus)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('AddlStateFilingStatus');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('AddlStateWithholdingAllowance')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetAddlStateWithholdingAllowance(val));
      });
    this.store
      .select(TaxesFormSelectors.addlStateWithholdingAllowance)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('AddlStateWithholdingAllowance');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('AddlStateAddlAllowances')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetAddlStateAddlAllowances(val));
      });
    this.store
      .select(TaxesFormSelectors.addlStateAddlAllowances)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('AddlStateAddlAllowances');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('AddlStateAddlWithholdingType')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetAddlStateAddlWithholdingType(val));
      });
    this.store
      .select(TaxesFormSelectors.addlStateAddlWithholdingType)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('AddlStateAddlWithholdingType');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('AddlStateAddlWithholdingValue')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetAddlStateAddlWithholdingValue(val));
      });
    this.store
      .select(TaxesFormSelectors.addlStateAddlWithholdingValue)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('AddlStateAddlWithholdingValue');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('IsExemptFromWithholding')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetIsExemptFromWithholding(val));
      });
    this.store
      .select(TaxesFormSelectors.isExemptFromWithholding)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('IsExemptFromWithholding');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('W4Version')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetW4Version(val));
      });
    this.store
      .select(TaxesFormSelectors.W4Version)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('W4Version');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('IsHigherWithholding')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetIsHigherWithholding(val));
      });
    this.store
      .select(TaxesFormSelectors.isHigherWithholding)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('IsHigherWithholding');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('ChildTaxCredit')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetChildTaxCredit(val));
      });
    this.store
      .select(TaxesFormSelectors.childTaxCredit)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('ChildTaxCredit');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('OtherIncome')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetOtherIncome(val));
      });
    this.store
      .select(TaxesFormSelectors.otherIncome)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('OtherIncome');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('WageDeduction')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetWageDeduction(val));
      });
    this.store
      .select(TaxesFormSelectors.wageDeduction)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('WageDeduction');
        if (control.value !== val) {
          control.setValue(val, { emitEvent: false });
        }
      });
    this.taxesForm
      .get('Locals')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        this.store.dispatch(new SetLocals(val));
      });
    this.store
      .select(TaxesFormSelectors.locals)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(val => {
        const control = this.taxesForm.get('Locals') as FormArray;
        if (!_.isEqual(control.value, val)) {
          control.clear({ emitEvent: false });
          val.forEach(x => {
            control.push(
              this.formBuilder.group({
                LocalTypeId: this.formBuilder.control(x.LocalTypeId),
                TaxCode: this.formBuilder.control(x.TaxCode),
                WithholdingAllowances: this.formBuilder.control(x.WithholdingAllowances),
                FilingStatus: this.formBuilder.control(x.FilingStatus),
                County: this.formBuilder.control(x.County),
                Municipality: this.formBuilder.control(x.Municipality),
                SchoolDistrict: this.formBuilder.control(x.SchoolDistrict)
              }),
              { emitEvent: false }
            );
          });
        }
      });
  }

  // #endregion
}
